/**
 * CPA-ONBOARDING-1 — the soft gate.
 *
 * A firm whose profile is missing or still a draft gets a prompt, NOT a wall.
 * A CPA signing in to look at an existing deal must not be trapped behind a
 * form, so this is a dismissible banner: it points at /firm-profile and gets
 * out of the way. Dismissal is per-session (sessionStorage) — it comes back on
 * the next sign-in, which is the nagging cadence the brief asked for.
 *
 * Silent on any error: a backend hiccup must never plant a scary banner across
 * every page.
 */
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'

const DISMISS_KEY = 'firmProfileGateDismissed'

/** Mirrors _FIRM_PROFILE_EDIT_ROLES in apps/api/routers/brands.py — nobody else
 *  can act on this prompt, so nobody else should see it. */
const EDIT_ROLES = new Set(['super_admin', 'cpa', 'firm', 'tester'])

export function FirmProfileGate() {
  const { user } = useAuth()
  const location = useLocation()
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1',
  )

  const eligible = EDIT_ROLES.has(user?.role ?? '')

  useEffect(() => {
    if (!eligible || dismissed) return
    let cancelled = false
    apiClient.get<{ needs_onboarding: boolean }>('/v1/firm-profile')
      .then(res => { if (!cancelled) setNeedsOnboarding(res.data.needs_onboarding) })
      .catch(() => { /* backend unavailable — say nothing */ })
    return () => { cancelled = true }
  }, [eligible, dismissed])

  // Suppressed on the page it points at — a banner telling you to go where you
  // already are is noise.
  if (!eligible || dismissed || !needsOnboarding || location.pathname === '/firm-profile') {
    return null
  }

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, '1')
    setDismissed(true)
  }

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 text-sm"
      style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d', color: '#78350f' }}
    >
      <span className="flex-1 min-w-[200px]">
        Your firm profile isn’t active yet. Until it is, reports and letters carry
        the platform default instead of your firm’s name and license details.
      </span>
      <Link to="/firm-profile">
        <Button size="sm">Complete firm profile</Button>
      </Link>
      <Button size="sm" variant="ghost" onClick={dismiss}>
        Later
      </Button>
    </div>
  )
}
